/**
 * @module features/upload/upload-modal
 * @description Manages the upload modal UI initialization and event listeners
 */

import {
    handleDragOver,
    handleDragLeave,
    handleFileDrop,
    handleFileSelection
} from './upload-form.js';
import { getAcceptAttribute, describeUploadConstraints } from './upload-constraints.js';

// DOM element references
let uploadModal = null;
let uploadDropZone = null;
let fileInput = null;
let uploadFilesContainer = null;
let uploadFilesList = null;
let uploadError = null;
let closeUploadModal = null;
let cancelUploadBtn = null;
let startUploadBtn = null;
let fileSelectBtn = null;

// Callbacks
let onCloseCallback = null;
let onUploadStartCallback = null;

// Track if event listeners have been set up
let eventListenersInitialized = false;

/**
 * Initialize upload modal elements
 */
export function initializeUploadElements() {
    uploadModal = document.getElementById('upload-modal');
    uploadDropZone = document.getElementById('upload-drop-zone');
    fileInput = document.getElementById('file-input');
    uploadFilesContainer = document.getElementById('upload-files-container');
    uploadFilesList = document.getElementById('upload-files-list');
    uploadError = document.getElementById('upload-error');
    closeUploadModal = document.getElementById('close-upload-modal');
    cancelUploadBtn = document.getElementById('cancel-upload-btn');
    startUploadBtn = document.getElementById('start-upload-btn');
    fileSelectBtn = document.getElementById('file-select-btn');

    if (uploadModal && !eventListenersInitialized) {
        setupUploadEventListeners();
        eventListenersInitialized = true;
    }
}

/**
 * Setup all upload event listeners
 */
function setupUploadEventListeners() {
    // Modal controls
    closeUploadModal.addEventListener('click', handleModalClose);
    cancelUploadBtn.addEventListener('click', handleModalClose);

    // Click outside modal to close
    uploadModal.addEventListener('click', (e) => {
        if (e.target === uploadModal) {
            handleModalClose();
        }
    });

    // File selection
    fileSelectBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        fileInput.click();
    });
    fileInput.addEventListener('change', handleFileSelection);

    // Drag and drop
    uploadDropZone.addEventListener('dragover', (e) => handleDragOver(e, uploadDropZone));
    uploadDropZone.addEventListener('dragleave', (e) => handleDragLeave(e, uploadDropZone));
    uploadDropZone.addEventListener('drop', (e) => handleFileDrop(e, uploadDropZone));

    // Upload button
    startUploadBtn.addEventListener('click', handleUploadStart);

    // Prevent default drag behaviors on window
    window.addEventListener('dragover', (e) => e.preventDefault());
    window.addEventListener('drop', (e) => e.preventDefault());
}

/**
 * Handle modal close
 */
function handleModalClose() {
    if (onCloseCallback) {
        onCloseCallback();
    }
}

/**
 * Handle upload start
 */
function handleUploadStart() {
    if (onUploadStartCallback) {
        onUploadStartCallback();
    }
}

/**
 * Set callback for modal close
 * @param {Function} callback - Callback function
 */
export function setOnCloseCallback(callback) {
    onCloseCallback = callback;
}

/**
 * Set callback for upload start
 * @param {Function} callback - Callback function
 */
export function setOnUploadStartCallback(callback) {
    onUploadStartCallback = callback;
}

/**
 * Show the upload modal
 */
export function showModal() {
    if (uploadModal) {
        uploadModal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
    }
}

/**
 * Hide the upload modal
 */
export function hideModal() {
    if (uploadModal) {
        uploadModal.classList.add('hidden');
        document.body.style.overflow = '';
    }
}

/**
 * Reset the upload UI to initial state
 */
export function resetUploadUI() {
    if (uploadFilesList) {
        uploadFilesList.classList.add('hidden');
    }
    if (uploadFilesContainer) {
        uploadFilesContainer.innerHTML = '';
    }
    if (startUploadBtn) {
        startUploadBtn.disabled = true;
    }
    hideUploadError();
}

/**
 * Show upload error message
 * @param {string} message - Error message to display
 */
export function showUploadError(message) {
    if (uploadError) {
        uploadError.textContent = message;
        uploadError.classList.remove('hidden');
    }
}

/**
 * Hide upload error message
 */
export function hideUploadError() {
    if (uploadError) {
        uploadError.classList.add('hidden');
    }
}

/**
 * Update upload button state
 * @param {boolean} enabled - Whether button should be enabled
 */
export function updateUploadButton(enabled) {
    if (startUploadBtn) {
        startUploadBtn.disabled = !enabled;
    }
}

/**
 * Set upload buttons state (for during upload)
 * @param {boolean} uploading - Whether upload is in progress
 */
export function setUploadingState(uploading) {
    if (startUploadBtn) {
        startUploadBtn.disabled = uploading;
    }
    if (cancelUploadBtn) {
        cancelUploadBtn.disabled = uploading;
    }
}

/**
 * Apply the API-reported upload constraints to the modal.
 *
 * Sets the file picker's accept filter and the drop zone hint. Note that the accept
 * attribute is advisory and does not apply to drag and drop, so file selection is
 * still checked in upload-form.js.
 */
export function applyUploadConstraintsToUI() {
    if (fileInput) {
        fileInput.accept = getAcceptAttribute();
    }

    const constraintsHint = document.getElementById('upload-constraints-hint');
    if (constraintsHint) {
        constraintsHint.textContent = describeUploadConstraints();
    }
}

/**
 * Get DOM element references
 * @returns {Object} Object containing all DOM element references
 */
export function getDOMElements() {
    return {
        uploadModal,
        uploadDropZone,
        fileInput,
        uploadFilesContainer,
        uploadFilesList,
        uploadError,
        closeUploadModal,
        cancelUploadBtn,
        startUploadBtn,
        fileSelectBtn
    };
}
